import { LucideIcon, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AnimateOnScroll } from "@/components/AnimateOnScroll";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  badge?: {
    icon?: LucideIcon;
    text: string;
    color?: string;
  };
  title?: string;
  subtitle?: string;
  items: FAQItem[];
  accentColor?: string;
  showContactCTA?: boolean;
  contactCTAText?: string;
  contactCTAButtonText?: string;
  onContactClick?: () => void;
}

export const FAQSection = ({
  badge,
  title = "Frequently Asked Questions",
  subtitle = "Got questions? We've got answers. If you don't see your question here, feel free to reach out to our support team.",
  items,
  accentColor = "blue",
  showContactCTA = true,
  contactCTAText = "Still have questions? We're here to help!",
  contactCTAButtonText = "Contact Support",
  onContactClick,
}: FAQSectionProps) => {
  const BadgeIcon = badge?.icon || HelpCircle;
  
  // Dynamic accent color classes
  const accentClasses: Record<string, { badge: string; border: string; icon: string; button: string }> = {
    blue: {
      badge: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      border: "data-[state=open]:border-blue-300",
      icon: "[&[data-state=open]>svg]:text-blue-500",
      button: "from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 shadow-blue-500/30 hover:shadow-blue-500/50",
    },
    green: {
      badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      border: "data-[state=open]:border-emerald-300",
      icon: "[&[data-state=open]>svg]:text-emerald-500",
      button: "from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-emerald-500/30 hover:shadow-emerald-500/50",
    },
    emerald: {
      badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      border: "data-[state=open]:border-emerald-300",
      icon: "[&[data-state=open]>svg]:text-emerald-500",
      button: "from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-emerald-500/30 hover:shadow-emerald-500/50",
    },
    sky: {
      badge: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
      border: "data-[state=open]:border-sky-300",
      icon: "[&[data-state=open]>svg]:text-sky-500",
      button: "from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 shadow-sky-500/30 hover:shadow-sky-500/50",
    },
    purple: {
      badge: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
      border: "data-[state=open]:border-purple-300",
      icon: "[&[data-state=open]>svg]:text-purple-500",
      button: "from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 shadow-purple-500/30 hover:shadow-purple-500/50",
    },
  };

  const colors = accentClasses[accentColor] || accentClasses.blue;

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <AnimateOnScroll animation="fade-up">
          <div className="max-w-3xl mx-auto">
            {/* FAQ Header */}
            <div className="text-center mb-8 sm:mb-10 md:mb-12">
              {badge && (
                <div className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4 ${colors.badge}`}>
                  <BadgeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>{badge.text}</span>
                </div>
              )}
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 sm:mb-4">
                {title}
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                {subtitle}
              </p>
            </div>

            {/* FAQ Accordion */}
            <Accordion type="single" collapsible className="space-y-3 sm:space-y-4">
              {items.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className={`bg-card border border-border rounded-xl sm:rounded-2xl ${colors.border} transition-colors duration-300 !border-b pointer-events-auto`}
                >
                  <AccordionTrigger 
                    className={`px-4 sm:px-6 py-4 sm:py-5 text-left text-foreground hover:no-underline hover:text-foreground ${colors.icon} cursor-pointer`}
                  >
                    <span className="text-base sm:text-lg font-semibold pr-4">{item.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <div className="border-t border-border pt-4 sm:pt-5">
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{item.answer}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Contact Support CTA */}
            {showContactCTA && (
              <div className="text-center mt-8 sm:mt-10 md:mt-12">
                <p className="text-muted-foreground mb-4 sm:mb-5 text-sm sm:text-base">
                  {contactCTAText}
                </p>
                <Button 
                  onClick={onContactClick}
                  className={`bg-gradient-to-r ${colors.button} text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105`}
                >
                  {contactCTAButtonText}
                </Button>
              </div>
            )}
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};
